import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { useAuth } from '../context/AuthContext';
import { useCustomers } from '../context/CustomerContext';
import { displayName } from '../utils/customerDisplay';
import './CustomerHealthDashboard.css';
import Alert from './Alert';
import LoadingSpinner from './LoadingSpinner';
import Button from './Button';
import {
  ActivityChart,
  METRICS,
  BUCKETS,
  defaultBucketForRange,
  MetricKey,
  TimeSeriesResponse,
} from './UsageActivityChart';

interface HeadlineMetrics {
  dau: number;
  wau: number;
  mau: number;
  total_assigned_devices: number;
  mau_utilization: number;
}

interface DailyTrend {
  date: string;
  active_devices: number;
}

interface OrgMetrics {
  org_user_id: string;
  org_name: string;
  customer_tag: string | null;
  subscription_status: string | null;
  total_devices: number;
  dau: number;
  wau: number;
  mau: number;
  utilization: number;
  daily_trend: DailyTrend[];
  // Phase 1 renewal/ACV fields (null when no Stripe data)
  end_date: string | null;        // ISO YYYY-MM-DD
  disposition: 'auto_renew' | 'cancels_at_period_end' | 'cancels_on_specific_date' | 'unknown' | null;
  monthly_rate: number | null;
  acv: number | null;
  contract_units: number | null;
  // Phase 5 — deployment vs. contract (lifted from /customers Customers tab)
  active_units: number;
  active_docks: number;
  // Salesforce account / AE info + computed subscription end, populated by the
  // SF sync (null if unmatched). Subscription_end is filled even for customers
  // without an SF link, since the source includes Stripe-only data.
  salesforce: {
    account_id: string | null;
    account_name: string | null;
    owner_name: string | null;
    owner_email: string | null;
    net_acv: number | null;
    total_units: number | null;
    package: string | null;
    last_signed_opp: {
      id: string | null;
      name: string | null;
      close_date: string | null;     // ISO YYYY-MM-DD
      term_months: number | null;
      amount: number | null;
    } | null;
    subscription_end: {
      date: string | null;            // ISO YYYY-MM-DD
      source:
        | 'cancel_at'
        | 'period_end'
        | 'invoice_supply'
        | 'needs_fixing'
        | null
        | string;
    };
  } | null;
}

interface UsageData {
  headline: HeadlineMetrics;
  daily_trend: DailyTrend[];
  per_org: OrgMetrics[];
  window: {
    start: string;
    end: string;
    days: number;
  };
  total_shifts: number;
}

type DatePreset = '7d' | '30d' | 'qtd' | 'ytd' | '90d' | '180d' | 'custom';

const formatDate = (d: Date): string => d.toISOString().split('T')[0];

const LineChart: React.FC<{
  data: DailyTrend[];
  maxValue: number;
  labelInterval: number;
}> = ({ data, maxValue, labelInterval }) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const [dragStart, setDragStart] = useState<number | null>(null);
  const [selection, setSelection] = useState<{ from: number; to: number } | null>(null);
  const isDragging = React.useRef(false);

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver(entries => {
      setContainerWidth(entries[0].contentRect.width);
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  if (data.length === 0 || containerWidth === 0) {
    return <div ref={containerRef} className="line-chart-container" style={{ minHeight: 200 }} />;
  }

  const pad = { top: 16, right: 8, bottom: 28, left: 36 };
  const w = containerWidth;
  const h = 180;
  const chartW = w - pad.left - pad.right;
  const chartH = h - pad.top - pad.bottom;
  const bottom = pad.top + chartH;

  const points = data.map((d, i) => ({
    x: pad.left + (i / (data.length - 1 || 1)) * chartW,
    y: pad.top + chartH - (d.active_devices / maxValue) * chartH,
  }));

  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  const areaPath = `${linePath} L${points[points.length - 1].x.toFixed(1)},${bottom} L${points[0].x.toFixed(1)},${bottom} Z`;

  const yTicks = [0, Math.round(maxValue / 2), maxValue];

  const xLabels = data
    .map((d, i) => ({ index: i, label: d.date.slice(5) }))
    .filter((_, i) => i % labelInterval === 0 || i === data.length - 1);

  const closestIndex = (mouseX: number): number => {
    let closest = 0;
    let minDist = Infinity;
    for (let i = 0; i < points.length; i++) {
      const dist = Math.abs(points[i].x - mouseX);
      if (dist < minDist) { minDist = dist; closest = i; }
    }
    return closest;
  };

  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const idx = closestIndex(e.clientX - rect.left);
    setHoverIdx(idx);
    if (isDragging.current && dragStart !== null) {
      // Live preview of selection while dragging
      const from = Math.min(dragStart, idx);
      const to = Math.max(dragStart, idx);
      if (from !== to) setSelection({ from, to });
    }
  };

  const handleMouseDown = (e: React.MouseEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const idx = closestIndex(e.clientX - rect.left);
    isDragging.current = true;
    setDragStart(idx);
    setSelection(null);
  };

  const handleMouseUp = () => {
    if (isDragging.current && dragStart !== null && hoverIdx !== null && dragStart !== hoverIdx) {
      const from = Math.min(dragStart, hoverIdx);
      const to = Math.max(dragStart, hoverIdx);
      setSelection({ from, to });
    }
    isDragging.current = false;
    setDragStart(null);
  };

  const clearSelection = () => {
    setSelection(null);
    setDragStart(null);
    isDragging.current = false;
  };

  // Selection stats
  const selData = selection ? {
    fromDate: data[selection.from].date,
    toDate: data[selection.to].date,
    fromVal: data[selection.from].active_devices,
    toVal: data[selection.to].active_devices,
    days: selection.to - selection.from,
  } : null;

  const selChange = selData ? selData.toVal - selData.fromVal : 0;
  const selPct = selData && selData.fromVal > 0
    ? ((selData.toVal - selData.fromVal) / selData.fromVal * 100)
    : 0;

  // Build selection highlight clip path
  let selAreaPath = '';
  if (selection) {
    const selPoints = points.slice(selection.from, selection.to + 1);
    selAreaPath = selPoints.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
    selAreaPath += ` L${selPoints[selPoints.length - 1].x.toFixed(1)},${bottom} L${selPoints[0].x.toFixed(1)},${bottom} Z`;
  }

  const hp = hoverIdx !== null ? { ...points[hoverIdx], ...data[hoverIdx] } : null;

  return (
    <div ref={containerRef} className="line-chart-container">
      <svg
        width={w}
        height={h}
        className={`line-chart ${isDragging.current ? 'dragging' : ''}`}
        onMouseMove={handleMouseMove}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={() => { setHoverIdx(null); handleMouseUp(); }}
      >
        {/* Grid lines */}
        {yTicks.map(v => {
          const y = pad.top + chartH - (v / maxValue) * chartH;
          return (
            <g key={v}>
              <line x1={pad.left} x2={w - pad.right} y1={y} y2={y} className="grid-line" />
              <text x={pad.left - 6} y={y + 3} className="y-label">{v}</text>
            </g>
          );
        })}

        {/* Base area fill */}
        <path d={areaPath} className="chart-area" />

        {/* Selection highlight */}
        {selection && (
          <g>
            <rect
              x={points[selection.from].x}
              y={pad.top}
              width={points[selection.to].x - points[selection.from].x}
              height={chartH}
              className="selection-bg"
            />
            <path d={selAreaPath} className="selection-area" />
            <line x1={points[selection.from].x} x2={points[selection.from].x} y1={pad.top} y2={bottom} className="selection-edge" />
            <line x1={points[selection.to].x} x2={points[selection.to].x} y1={pad.top} y2={bottom} className="selection-edge" />
            <circle cx={points[selection.from].x} cy={points[selection.from].y} r={5} className="selection-dot" />
            <circle cx={points[selection.to].x} cy={points[selection.to].y} r={5} className="selection-dot" />
          </g>
        )}

        {/* Line */}
        <path d={linePath} className="chart-line" />

        {/* Hover indicator (hide when actively dragging) */}
        {hp && !isDragging.current && (
          <g>
            <line x1={hp.x} x2={hp.x} y1={pad.top} y2={bottom} className="tooltip-line" />
            <circle cx={hp.x} cy={hp.y} r={5} className="chart-dot-active" />
          </g>
        )}

        {/* X-axis labels */}
        {xLabels.map(({ index, label }) => (
          <text key={index} x={points[index].x} y={h - 6} className="x-label">{label}</text>
        ))}
      </svg>

      {/* Hover tooltip (hide during drag) */}
      {hp && !isDragging.current && !selection && (
        <div className="chart-tooltip" style={{ left: hp.x, top: hp.y - 8 }}>
          <div className="chart-tooltip-value">{hp.active_devices} devices</div>
          <div className="chart-tooltip-date">{hp.date}</div>
        </div>
      )}

      {/* Selection summary card */}
      {selData && selection && (
        <div className="selection-summary">
          <div className="selection-range">
            <span>{selData.fromDate}</span>
            <span className="selection-arrow">&rarr;</span>
            <span>{selData.toDate}</span>
            <span className="selection-days">({selData.days} days)</span>
          </div>
          <div className="selection-metrics">
            <div className="selection-metric">
              <span className="selection-metric-label">Start</span>
              <span className="selection-metric-value">{selData.fromVal}</span>
            </div>
            <div className="selection-metric">
              <span className="selection-metric-label">End</span>
              <span className="selection-metric-value">{selData.toVal}</span>
            </div>
            <div className="selection-metric">
              <span className="selection-metric-label">Change</span>
              <span className={`selection-metric-value ${selChange > 0 ? 'positive' : selChange < 0 ? 'negative' : ''}`}>
                {selChange > 0 ? '+' : ''}{selChange} ({selPct > 0 ? '+' : ''}{selPct.toFixed(1)}%)
              </span>
            </div>
          </div>
          <button className="selection-clear" onClick={clearSelection} aria-label="Clear selection">&times;</button>
        </div>
      )}

      {!selection && (
        <div className="chart-hint">Click and drag to select a range</div>
      )}
    </div>
  );
};

interface EmailTemplate {
  id: number;
  slug: string;
  label: string;
  state_tag: string | null;
}

interface RenderedEmail {
  subject: string;
  body_html: string;
  placeholder_count: number;
  template: { id: number; slug: string; label: string };
}

const CustomerHealthDashboard: React.FC = () => {
  const { username } = useAuth();
  const { customers } = useCustomers();
  // customer_tag is a stable join key; the displayed name comes from the
  // customer's Stripe description (which refreshes on every sync) so a rename
  // in Stripe shows up here. Falls back to the raw tag for orphans.
  const customerByTag = useMemo(
    () => new Map(customers.map(c => [c.customer_tag, c])),
    [customers],
  );
  const tagName = useCallback((tag: string | null): string | null => {
    if (!tag) return null;
    const c = customerByTag.get(tag);
    return c ? displayName(c) : tag;
  }, [customerByTag]);
  const [data, setData] = useState<UsageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortField, setSortField] = useState<keyof OrgMetrics>('end_date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [filter, setFilter] = useState<'all' | 'healthy' | 'at-risk' | 'inactive'>('all');
  // Phase 1: default to "active" so the working list excludes trial customers
  // (managed in /trials) and unmapped/canceled noise.
  const [subFilter, setSubFilter] = useState<'all' | 'trialing' | 'active' | 'canceled' | 'unmapped'>('active');
  const [selectedOrg, setSelectedOrg] = useState<OrgMetrics | null>(null);

  // Phase 3 — email template feature
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [rendered, setRendered] = useState<RenderedEmail | null>(null);
  const [renderLoading, setRenderLoading] = useState(false);
  const [renderError, setRenderError] = useState<string | null>(null);
  const [copyStatus, setCopyStatus] = useState<string | null>(null);

  // Phase 4 — usage charts (folded in from /stats)
  const [usageStats, setUsageStats] = useState<any | null>(null);
  const [usageStatsLoading, setUsageStatsLoading] = useState(false);
  const [usageSeries, setUsageSeries] = useState<TimeSeriesResponse | null>(null);
  const [usageSeriesLoading, setUsageSeriesLoading] = useState(false);
  const [usageSeriesUnavailable, setUsageSeriesUnavailable] = useState(false);
  const [usageMetric, setUsageMetric] = useState<MetricKey>('actions');
  const [usageBucket, setUsageBucket] = useState<'day' | 'week' | 'month'>('week');

  // Date range state — default to 90d so the per-customer time-series chart
  // auto-buckets to weekly (defaultBucketForRange returns 'week' for ≤180d).
  const [preset, setPreset] = useState<DatePreset>('90d');
  const today = formatDate(new Date());
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 90);
    return formatDate(d);
  });
  const [endDate, setEndDate] = useState(today);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams({ start: startDate, end: endDate });
      const response = await fetch(`${API_BASE_URL}/api/device-usage?${params}`);
      if (!response.ok) throw new Error('Failed to fetch device usage data');
      const result = await response.json();
      setData(result);
    } catch (err: any) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch email templates once on mount
  useEffect(() => {
    fetch(`${API_BASE_URL}/api/customer-health/email-templates`)
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(d => setTemplates(d.templates || []))
      .catch(() => {});
  }, []);

  // Reset email panel state whenever a new customer is opened
  useEffect(() => {
    if (!selectedOrg) {
      setSelectedTemplateId(null);
      setRendered(null);
      setRenderError(null);
      setCopyStatus(null);
      return;
    }
    // Auto-pick the renewal_qbr template by default if available; falls back to first
    if (templates.length && selectedTemplateId === null) {
      const def = templates.find(t => t.slug === 'renewal_qbr') ?? templates[0];
      setSelectedTemplateId(def.id);
    }
  }, [selectedOrg, templates, selectedTemplateId]);

  // Phase 4 — fetch orgstats snapshot + time-series when a customer is opened.
  // Uses the dashboard's current date range for both. Unmount/swap clears state.
  useEffect(() => {
    if (!selectedOrg || !selectedOrg.org_name) {
      setUsageStats(null);
      setUsageSeries(null);
      setUsageSeriesUnavailable(false);
      return;
    }
    const orgName = selectedOrg.org_name;
    const from = startDate;
    const to = endDate;

    setUsageStatsLoading(true);
    // Backend-cached (60-min TTL) so repeat opens are sub-millisecond.
    axios.get(`${API_BASE_URL}/api/customer-health/usage-stats`, {
      params: { org: orgName, start: from, end: to },
    })
      .then(r => setUsageStats(r.data))
      .catch(() => setUsageStats(null))
      .finally(() => setUsageStatsLoading(false));

    // Auto-pick a sensible bucket from the date range on each open
    const autoBucket = defaultBucketForRange(from, to);
    setUsageBucket(autoBucket);

    setUsageSeriesLoading(true);
    setUsageSeriesUnavailable(false);
    axios.get(`${API_BASE_URL}/api/customer-health/usage-timeseries`, {
      params: { org: orgName, from, to, bucket: autoBucket },
    })
      .then(r => setUsageSeries(r.data))
      .catch(err => {
        if (err?.response?.status === 404) {
          setUsageSeriesUnavailable(true);
        }
        setUsageSeries(null);
      })
      .finally(() => setUsageSeriesLoading(false));
  }, [selectedOrg, startDate, endDate]);

  // Re-fetch the time-series alone when the user changes bucket (without re-fetching stats)
  useEffect(() => {
    if (!selectedOrg || !selectedOrg.org_name || !startDate || !endDate) return;
    setUsageSeriesLoading(true);
    setUsageSeriesUnavailable(false);
    axios.get(`${API_BASE_URL}/api/customer-health/usage-timeseries`, {
      params: { org: selectedOrg.org_name, from: startDate, to: endDate, bucket: usageBucket },
    })
      .then(r => setUsageSeries(r.data))
      .catch(err => {
        if (err?.response?.status === 404) setUsageSeriesUnavailable(true);
        setUsageSeries(null);
      })
      .finally(() => setUsageSeriesLoading(false));
    // We deliberately don't include selectedOrg/startDate/endDate here — those
    // are handled by the effect above. This one only re-fires on bucket change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usageBucket]);

  // Render the email whenever the org / template / usage stats change.
  // usageStats is part of the dep list so the email re-renders once
  // orgstats arrives — escalations and reports come from there.
  useEffect(() => {
    if (!selectedOrg || selectedTemplateId === null) return;
    setRenderLoading(true);
    setRenderError(null);
    setCopyStatus(null);
    const payload = {
      customer_tag: selectedOrg.customer_tag,
      template_id: selectedTemplateId,
      ae_name: username || null,
      end_date: selectedOrg.end_date,
      monthly_rate: selectedOrg.monthly_rate,
      acv: selectedOrg.acv,
      contract_units: selectedOrg.contract_units,
      devices_deployed: selectedOrg.total_devices,
      mau: selectedOrg.mau,
      utilization_pct: selectedOrg.utilization,
      // From orgstats fetch on panel open. null while loading → bracketed
      // [ESCALATIONS] / [REPORTS GENERATED] placeholder for the AE.
      escalations: usageStats?.escalations ?? null,
      reports_generated: usageStats?.reports ?? null,
      activation_rate_pct:
        usageStats?.activation_rate != null
          ? Math.round(usageStats.activation_rate * 100)
          : null,
    };
    fetch(`${API_BASE_URL}/api/customer-health/render-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(async r => {
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          throw new Error(err.error || `Render failed (${r.status})`);
        }
        return r.json();
      })
      .then((d: RenderedEmail) => setRendered(d))
      .catch(e => setRenderError(e instanceof Error ? e.message : 'Render failed'))
      .finally(() => setRenderLoading(false));
  }, [selectedOrg, selectedTemplateId, username, usageStats]);

  // execCommand fallback for non-secure contexts (HTTP, Tailscale hostnames, etc.)
  // where navigator.clipboard is unavailable. Uses a temporary contentEditable
  // div so HTML formatting carries over to Gmail/Outlook on paste.
  const copyHtmlViaExecCommand = (html: string): boolean => {
    try {
      const container = document.createElement('div');
      container.contentEditable = 'true';
      container.innerHTML = html;
      // Off-screen but selectable
      container.style.position = 'fixed';
      container.style.left = '-9999px';
      container.style.top = '0';
      container.style.opacity = '0';
      document.body.appendChild(container);
      const range = document.createRange();
      range.selectNodeContents(container);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
      const ok = document.execCommand('copy');
      selection?.removeAllRanges();
      document.body.removeChild(container);
      return ok;
    } catch {
      return false;
    }
  };

  const copyTextViaExecCommand = (text: string): boolean => {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      ta.style.top = '0';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  };

  const copyHtml = useCallback(async () => {
    if (!rendered) return;
    // 1) Modern Clipboard API — rich HTML (Gmail/Outlook keep formatting)
    if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
      try {
        const blobHtml = new Blob([rendered.body_html], { type: 'text/html' });
        const blobText = new Blob([rendered.body_html.replace(/<[^>]+>/g, '')], { type: 'text/plain' });
        await navigator.clipboard.write([
          new ClipboardItem({ 'text/html': blobHtml, 'text/plain': blobText }),
        ]);
        setCopyStatus('Body copied');
        setTimeout(() => setCopyStatus(null), 2500);
        return;
      } catch { /* fall through to fallback */ }
    }
    // 2) execCommand with rich HTML (works in non-secure contexts)
    if (copyHtmlViaExecCommand(rendered.body_html)) {
      setCopyStatus('Body copied');
      setTimeout(() => setCopyStatus(null), 2500);
      return;
    }
    // 3) Last-ditch plain-text writeText
    try {
      await navigator.clipboard?.writeText(rendered.body_html);
      setCopyStatus('Body copied (plain text)');
      setTimeout(() => setCopyStatus(null), 2500);
    } catch {
      setCopyStatus('Copy failed — select preview text and copy manually');
    }
  }, [rendered]);

  const copySubject = useCallback(async () => {
    if (!rendered) return;
    // 1) Modern Clipboard API
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(rendered.subject);
        setCopyStatus('Subject copied');
        setTimeout(() => setCopyStatus(null), 2500);
        return;
      } catch { /* fall through */ }
    }
    // 2) execCommand fallback
    if (copyTextViaExecCommand(rendered.subject)) {
      setCopyStatus('Subject copied');
      setTimeout(() => setCopyStatus(null), 2500);
    } else {
      setCopyStatus('Copy failed');
    }
  }, [rendered]);

  const applyPreset = (p: DatePreset) => {
    setPreset(p);
    if (p === 'custom') return;
    const end = new Date();
    const start = new Date();
    if (p === 'qtd') {
      const q = Math.floor(start.getMonth() / 3);
      start.setMonth(q * 3, 1);
      start.setHours(0, 0, 0, 0);
    } else if (p === 'ytd') {
      start.setMonth(0, 1);
      start.setHours(0, 0, 0, 0);
    } else {
      const days = parseInt(p);
      start.setDate(start.getDate() - days);
    }
    setStartDate(formatDate(start));
    setEndDate(formatDate(end));
  };

  const handleSort = (field: keyof OrgMetrics) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const getHealthStatus = (org: OrgMetrics): 'healthy' | 'at-risk' | 'inactive' => {
    if (org.total_devices === 0) return 'inactive';
    if (org.utilization >= 50) return 'healthy';
    if (org.utilization > 0) return 'at-risk';
    return 'inactive';
  };

  const getSubBadge = (status: string | null) => {
    if (!status) return <span className="health-badge neutral">—</span>;
    const map: Record<string, { cls: string; label: string }> = {
      'trialing': { cls: 'health-badge blue', label: 'Trial' },
      'active': { cls: 'health-badge green', label: 'Paid' },
      'canceled': { cls: 'health-badge red', label: 'Canceled' },
      'past_due': { cls: 'health-badge orange', label: 'Past Due' },
      'unpaid': { cls: 'health-badge orange', label: 'Unpaid' },
    };
    const info = map[status] || { cls: 'health-badge neutral', label: status };
    return <span className={info.cls}>{info.label}</span>;
  };

  const getDispositionBadge = (disposition: OrgMetrics['disposition']) => {
    if (!disposition) return <span className="health-badge neutral">—</span>;
    const map: Record<NonNullable<OrgMetrics['disposition']>, { cls: string; label: string }> = {
      auto_renew: { cls: 'health-badge green', label: 'Auto-renew' },
      cancels_at_period_end: { cls: 'health-badge orange', label: 'Ends at period' },
      cancels_on_specific_date: { cls: 'health-badge red', label: 'Cancels' },
      unknown: { cls: 'health-badge neutral', label: '—' },
    };
    const info = map[disposition];
    return <span className={info.cls}>{info.label}</span>;
  };

  const formatEndDate = (iso: string | null) => {
    if (!iso) return <span className="no-match">—</span>;
    const d = new Date(iso + 'T00:00:00');
    if (isNaN(d.getTime())) return iso;
    const now = new Date();
    const days = Math.round((d.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    const label = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return (
      <span className="end-date-cell">
        {label}
        <span className={`days-badge ${days < 0 ? 'overdue' : days <= 90 ? 'soon' : ''}`}>
          {days < 0 ? `${-days}d ago` : `${days}d`}
        </span>
      </span>
    );
  };

  const formatAcv = (acv: number | null) => {
    if (acv === null || acv === undefined) return <span className="no-match">—</span>;
    if (acv >= 1000) return `$${(acv / 1000).toFixed(acv >= 10000 ? 0 : 1)}k`;
    return `$${acv.toFixed(0)}`;
  };

  const getHealthBadge = (status: string) => {
    const classes: Record<string, string> = {
      'healthy': 'health-badge green',
      'at-risk': 'health-badge orange',
      'inactive': 'health-badge red',
    };
    const labels: Record<string, string> = {
      'healthy': 'Healthy',
      'at-risk': 'At Risk',
      'inactive': 'Inactive',
    };
    return <span className={classes[status]}>{labels[status]}</span>;
  };

  // Compute x-axis label interval
  const getLabelInterval = (count: number): number => {
    if (count <= 14) return 2;
    if (count <= 30) return 7;
    if (count <= 60) return 14;
    return 21;
  };

  if (loading && !data) return <LoadingSpinner message="Loading device usage data..." />;

  if (error && !data) {
    return (
      <div className="customer-health">
        <Alert type="error">{error}</Alert>
        <Button variant="primary" size="sm" onClick={fetchData}>Retry</Button>
      </div>
    );
  }

  if (!data) return null;

  const { headline, daily_trend, per_org } = data;

  // Apply both filters to get the active org set (used for headline, trend, and table)
  const filteredOrgs = per_org.filter(org => {
    if (filter !== 'all' && getHealthStatus(org) !== filter) return false;
    if (subFilter === 'all') return true;
    if (subFilter === 'unmapped') return !org.customer_tag;
    return org.subscription_status === subFilter;
  });

  const isFiltered = filter !== 'all' || subFilter !== 'all';

  // Derive headline metrics from filtered orgs
  const filteredHeadline = isFiltered ? {
    dau: Math.round(filteredOrgs.reduce((s, o) => s + o.dau, 0) * 10) / 10,
    wau: filteredOrgs.reduce((s, o) => s + o.wau, 0),
    mau: filteredOrgs.reduce((s, o) => s + o.mau, 0),
    total_assigned_devices: filteredOrgs.reduce((s, o) => s + o.total_devices, 0),
    mau_utilization: (() => {
      const total = filteredOrgs.reduce((s, o) => s + o.total_devices, 0);
      const mau = filteredOrgs.reduce((s, o) => s + o.mau, 0);
      return total > 0 ? Math.round(mau / total * 1000) / 10 : 0;
    })(),
  } : headline;

  // Derive daily trend from filtered orgs
  const filteredTrend = isFiltered && filteredOrgs.length > 0
    ? daily_trend.map((d, i) => ({
        date: d.date,
        active_devices: filteredOrgs.reduce((s, o) => s + (o.daily_trend[i]?.active_devices || 0), 0),
      }))
    : daily_trend;

  const maxDevices = Math.max(...filteredTrend.map(d => d.active_devices), 1);
  const labelInterval = getLabelInterval(filteredTrend.length);

  const sortedOrgs = [...filteredOrgs].sort((a, b) => {
    // Sort the customer column by the same name we display, not the raw tag.
    const aVal = sortField === 'customer_tag' ? tagName(a.customer_tag) : a[sortField];
    const bVal = sortField === 'customer_tag' ? tagName(b.customer_tag) : b[sortField];
    // NULLS LAST for any sort field (renewal date is the obvious one but applies generally)
    const aNull = aVal === null || aVal === undefined;
    const bNull = bVal === null || bVal === undefined;
    if (aNull && bNull) return 0;
    if (aNull) return 1;
    if (bNull) return -1;
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    }
    const aStr = String(aVal);
    const bStr = String(bVal);
    return sortDir === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
  });

  const healthCounts = {
    healthy: per_org.filter(o => getHealthStatus(o) === 'healthy').length,
    atRisk: per_org.filter(o => getHealthStatus(o) === 'at-risk').length,
    inactive: per_org.filter(o => getHealthStatus(o) === 'inactive').length,
  };

  const subCounts = {
    trialing: per_org.filter(o => o.subscription_status === 'trialing').length,
    active: per_org.filter(o => o.subscription_status === 'active').length,
    canceled: per_org.filter(o => o.subscription_status === 'canceled').length,
    unmapped: per_org.filter(o => !o.customer_tag).length,
  };

  const sortIcon = (field: keyof OrgMetrics) => {
    if (sortField !== field) return '';
    return sortDir === 'asc' ? ' \u25B2' : ' \u25BC';
  };

  return (
    <div className="customer-health">
      <div className="customer-health-header">
        <h2>Customers</h2>
        <Button variant="secondary" size="sm" onClick={fetchData} loading={loading}>
          Refresh
        </Button>
      </div>

      {/* Date Range Controls */}
      <div className="date-range-controls">
        <div className="date-presets">
          {([
            ['7d', '7D'],
            ['30d', '30D'],
            ['qtd', 'QTD'],
            ['ytd', 'YTD'],
            ['90d', '90D'],
            ['180d', '6M'],
          ] as [DatePreset, string][]).map(([p, label]) => (
            <button
              key={p}
              className={`preset-btn ${preset === p ? 'active' : ''}`}
              onClick={() => applyPreset(p)}
            >
              {label}
            </button>
          ))}
          <button
            className={`preset-btn ${preset === 'custom' ? 'active' : ''}`}
            onClick={() => setPreset('custom')}
          >
            Custom
          </button>
        </div>
        {preset === 'custom' && (
          <div className="date-inputs">
            <label>
              From
              <input
                type="date"
                value={startDate}
                max={endDate}
                onChange={e => setStartDate(e.target.value)}
              />
            </label>
            <label>
              To
              <input
                type="date"
                value={endDate}
                min={startDate}
                max={today}
                onChange={e => setEndDate(e.target.value)}
              />
            </label>
          </div>
        )}
      </div>

      {error && <Alert type="warning">{error}</Alert>}

      {/* Headline Metrics */}
      <div className="summary-cards">
        <div className="summary-card blue">
          <div className="card-number">{filteredHeadline.dau}</div>
          <div className="card-label">DAU (7d avg)</div>
        </div>
        <div className="summary-card blue">
          <div className="card-number">{filteredHeadline.wau}</div>
          <div className="card-label">WAU</div>
        </div>
        <div className="summary-card blue">
          <div className="card-number">{filteredHeadline.mau}</div>
          <div className="card-label">MAU</div>
        </div>
        <div className="summary-card neutral">
          <div className="card-number">{filteredHeadline.total_assigned_devices}</div>
          <div className="card-label">Total Devices</div>
        </div>
        <div className={`summary-card ${filteredHeadline.mau_utilization >= 50 ? 'green' : filteredHeadline.mau_utilization > 0 ? 'orange' : 'red'}`}>
          <div className="card-number">{filteredHeadline.mau_utilization}%</div>
          <div className="card-label">MAU Utilization</div>
        </div>
      </div>

      {/* Health Status Summary */}
      <div className="health-summary">
        <button
          className={`health-filter ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All ({per_org.length})
        </button>
        <button
          className={`health-filter healthy ${filter === 'healthy' ? 'active' : ''}`}
          onClick={() => setFilter('healthy')}
        >
          Healthy ({healthCounts.healthy})
        </button>
        <button
          className={`health-filter at-risk ${filter === 'at-risk' ? 'active' : ''}`}
          onClick={() => setFilter('at-risk')}
        >
          At Risk ({healthCounts.atRisk})
        </button>
        <button
          className={`health-filter inactive ${filter === 'inactive' ? 'active' : ''}`}
          onClick={() => setFilter('inactive')}
        >
          Inactive ({healthCounts.inactive})
        </button>
      </div>

      {/* Subscription Status Filter */}
      <div className="health-summary">
        <button
          className={`health-filter ${subFilter === 'all' ? 'active' : ''}`}
          onClick={() => setSubFilter('all')}
        >
          All
        </button>
        <button
          className={`health-filter trial ${subFilter === 'trialing' ? 'active' : ''}`}
          onClick={() => setSubFilter('trialing')}
        >
          Trial ({subCounts.trialing})
        </button>
        <button
          className={`health-filter paid ${subFilter === 'active' ? 'active' : ''}`}
          onClick={() => setSubFilter('active')}
        >
          Paid ({subCounts.active})
        </button>
        <button
          className={`health-filter canceled-filter ${subFilter === 'canceled' ? 'active' : ''}`}
          onClick={() => setSubFilter('canceled')}
        >
          Canceled ({subCounts.canceled})
        </button>
        <button
          className={`health-filter unmapped-filter ${subFilter === 'unmapped' ? 'active' : ''}`}
          onClick={() => setSubFilter('unmapped')}
        >
          Unmapped ({subCounts.unmapped})
        </button>
      </div>

      {/* Daily Trend Chart */}
      <div className="trend-section">
        <h3>Daily Active Devices ({data.window.start} to {data.window.end})</h3>
        <LineChart key={`${data.window.start}-${data.window.end}-${filter}-${subFilter}`} data={filteredTrend} maxValue={maxDevices} labelInterval={labelInterval} />
      </div>

      {/* Per-Org Table */}
      <div className="org-table-section">
        <h3>Per-Customer Breakdown</h3>
        <div className="org-table-scroll">
        <table className="org-table">
          <thead>
            <tr>
              <th className="sortable customer-col" onClick={() => handleSort('customer_tag')}>
                Customer{sortIcon('customer_tag')}
              </th>
              <th className="sortable col-hide-sm" onClick={() => handleSort('subscription_status')}>
                Plan{sortIcon('subscription_status')}
              </th>
              <th className="sortable" onClick={() => handleSort('end_date')}>
                Renewal{sortIcon('end_date')}
              </th>
              <th className="col-hide-md">Disposition</th>
              <th className="sortable num" onClick={() => handleSort('acv')}>
                ACV{sortIcon('acv')}
              </th>
              <th className="col-hide-sm">Health</th>
              <th className="sortable num col-hide-xs" onClick={() => handleSort('total_devices')}>
                Devices{sortIcon('total_devices')}
              </th>
              <th className="sortable num col-hide-md" onClick={() => handleSort('dau')}>
                DAU{sortIcon('dau')}
              </th>
              <th className="sortable num col-hide-md" onClick={() => handleSort('wau')}>
                WAU{sortIcon('wau')}
              </th>
              <th className="sortable num" onClick={() => handleSort('mau')}>
                MAU{sortIcon('mau')}
              </th>
              <th className="sortable num" onClick={() => handleSort('utilization')}>
                Util %{sortIcon('utilization')}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedOrgs.length === 0 ? (
              <tr><td colSpan={11} className="empty-state">No customers match the filter</td></tr>
            ) : (
              sortedOrgs.map((org) => (
                <tr
                  key={org.org_user_id}
                  onClick={() => setSelectedOrg(org)}
                  style={{ cursor: 'pointer' }}
                >
                  <td className="customer-col" title={org.customer_tag ? `${tagName(org.customer_tag)} · ${org.org_name}` : org.org_name}>
                    {tagName(org.customer_tag) || <span className="no-match">{org.org_name}</span>}
                  </td>
                  <td className="col-hide-sm">{getSubBadge(org.subscription_status)}</td>
                  <td>{formatEndDate(org.end_date)}</td>
                  <td className="col-hide-md">{getDispositionBadge(org.disposition)}</td>
                  <td className="num">{formatAcv(org.acv)}</td>
                  <td className="col-hide-sm">{getHealthBadge(getHealthStatus(org))}</td>
                  <td className="num col-hide-xs">{org.total_devices}</td>
                  <td className="num col-hide-md">{org.dau}</td>
                  <td className="num col-hide-md">{org.wau}</td>
                  <td className="num">{org.mau}</td>
                  <td className="num">
                    <span className={`util-value ${org.utilization >= 50 ? 'high' : org.utilization > 0 ? 'medium' : 'low'}`}>
                      {org.utilization}%
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>

      <div className="data-footnote">
        Based on {data.total_shifts.toLocaleString()} shifts.
        Active = device had at least 1 shift in the timeframe.
      </div>

      {selectedOrg && (() => {
        const org = selectedOrg;
        const utilCls = org.utilization >= 50 ? 'high' : org.utilization > 0 ? 'medium' : 'low';
        const renderDispositionExplanation = () => {
          if (!org.end_date) return 'No subscription info on file.';
          const dateStr = new Date(org.end_date + 'T00:00:00').toLocaleDateString('en-US', {
            month: 'long', day: 'numeric', year: 'numeric',
          });
          switch (org.disposition) {
            case 'auto_renew':
              return <>Auto-renews on <strong>{dateStr}</strong>. No cancellation scheduled.</>;
            case 'cancels_at_period_end':
              return <>Will not renew. Subscription ends at the current period end on <strong>{dateStr}</strong>.</>;
            case 'cancels_on_specific_date':
              return <>Cancellation scheduled for <strong>{dateStr}</strong>.</>;
            case 'unknown':
            default:
              return 'No subscription info on file.';
          }
        };
        return (
          <div className="customer-detail-overlay" onClick={() => setSelectedOrg(null)}>
            <div className="customer-detail-panel" onClick={e => e.stopPropagation()}>
              <div className="customer-detail-header">
                <div>
                  <h3>{tagName(org.customer_tag) || org.org_name}</h3>
                  {org.customer_tag && <span className="customer-detail-tag">org: {org.org_name}</span>}
                </div>
                <button className="customer-detail-close" onClick={() => setSelectedOrg(null)} aria-label="Close">&times;</button>
              </div>

              <div className="customer-detail-meta">
                <div><span className="meta-label">Renewal</span>{formatEndDate(org.end_date)}</div>
                <div><span className="meta-label">Disposition</span>{getDispositionBadge(org.disposition)}</div>
                <div><span className="meta-label">Subscription</span>{getSubBadge(org.subscription_status)}</div>
                <div><span className="meta-label">Health</span>{getHealthBadge(getHealthStatus(org))}</div>
              </div>

              <div className="customer-detail-tiles">
                <div className="customer-tile">
                  <div className="tile-label">Devices deployed</div>
                  <div className="tile-value">{org.total_devices}</div>
                </div>
                <div className="customer-tile">
                  <div className="tile-label">DAU</div>
                  <div className="tile-value">{org.dau}</div>
                </div>
                <div className="customer-tile">
                  <div className="tile-label">WAU</div>
                  <div className="tile-value">{org.wau}</div>
                </div>
                <div className="customer-tile">
                  <div className="tile-label">MAU</div>
                  <div className="tile-value">{org.mau}</div>
                </div>
                <div className="customer-tile">
                  <div className="tile-label">Utilization</div>
                  <div className={`tile-value util-value ${utilCls}`}>{org.utilization}%</div>
                </div>
              </div>

              <div className="customer-detail-section">
                <h4>Subscription</h4>
                <dl className="customer-detail-dl">
                  <dt>Plan status</dt><dd>{org.subscription_status || '—'}</dd>
                  <dt>Monthly rate</dt><dd>{org.monthly_rate !== null ? `$${org.monthly_rate.toLocaleString()}` : '—'}</dd>
                  <dt>ACV</dt><dd>{formatAcv(org.acv)}</dd>
                  <dt>Contract</dt><dd>{org.contract_units ?? '—'} units</dd>
                  <dt>Active</dt><dd>{org.active_units} units {org.active_docks > 0 && <span className="muted">· {org.active_docks} docks</span>}</dd>
                  <dt>Assigned</dt><dd>{org.total_devices} {org.total_devices !== org.active_units && <span className="muted">(differs from active)</span>}</dd>
                  <dt>Renewal</dt><dd>{renderDispositionExplanation()}</dd>
                  {org.salesforce?.subscription_end && (
                    <>
                      <dt>Subscription Ends</dt>
                      <dd>
                        {org.salesforce.subscription_end.source === 'needs_fixing' ? (
                          <span className="sub-end-flag">⚠ Auto-renewing — needs end date</span>
                        ) : org.salesforce.subscription_end.date ? (
                          <>
                            <strong>
                              {new Date(org.salesforce.subscription_end.date + 'T00:00:00').toLocaleDateString('en-US', {
                                month: 'long', day: 'numeric', year: 'numeric',
                              })}
                            </strong>
                            <span className="muted"> · {
                              org.salesforce.subscription_end.source === 'cancel_at' ? 'subscription cancel_at'
                              : org.salesforce.subscription_end.source === 'period_end' ? 'will not renew at period end'
                              : org.salesforce.subscription_end.source === 'invoice_supply' ? 'last invoice supply'
                              : org.salesforce.subscription_end.source}
                            </span>
                          </>
                        ) : '—'}
                      </dd>
                    </>
                  )}
                  {org.salesforce?.last_signed_opp && (
                    <>
                      <dt>Last Signed Opportunity</dt>
                      <dd>
                        {org.salesforce.last_signed_opp.close_date && (
                          <>{new Date(org.salesforce.last_signed_opp.close_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</>
                        )}
                        {org.salesforce.last_signed_opp.term_months ? <span className="muted"> · {org.salesforce.last_signed_opp.term_months}mo</span> : null}
                        {org.salesforce.last_signed_opp.amount ? <span className="muted"> · ${org.salesforce.last_signed_opp.amount.toLocaleString()}</span> : null}
                      </dd>
                    </>
                  )}
                  {org.salesforce?.owner_name && (
                    <>
                      <dt>Account Owner</dt>
                      <dd>
                        {org.salesforce.owner_name}
                        {org.salesforce.owner_email && (
                          <> · <a href={`mailto:${org.salesforce.owner_email}`}>{org.salesforce.owner_email}</a></>
                        )}
                      </dd>
                    </>
                  )}
                </dl>
              </div>

              <div className="customer-detail-section">
                <h4>Daily Active Devices — {tagName(org.customer_tag) || org.org_name}</h4>
                <LineChart
                  key={org.org_user_id}
                  data={org.daily_trend}
                  maxValue={Math.max(...org.daily_trend.map(d => d.active_devices), 1)}
                  labelInterval={getLabelInterval(org.daily_trend.length)}
                />
              </div>

              {/* Phase 4 — Usage charts (folded in from /stats) */}
              <div className="customer-detail-section">
                <div className="usage-charts-header">
                  <h4>Usage charts ({data.window.start} to {data.window.end})</h4>
                </div>

                {/* Snapshot stats from orgstats */}
                {usageStatsLoading && !usageStats && <div className="muted">Loading usage stats…</div>}
                {usageStats && (
                  <div className="customer-detail-tiles">
                    <div className="customer-tile">
                      <div className="tile-label">Escalations</div>
                      <div className="tile-value">{usageStats.escalations?.toLocaleString() ?? '—'}</div>
                    </div>
                    <div className="customer-tile">
                      <div className="tile-label">Reports</div>
                      <div className="tile-value">{usageStats.reports?.toLocaleString() ?? '—'}</div>
                    </div>
                    <div className="customer-tile">
                      <div className="tile-label">Videos</div>
                      <div className="tile-value">{usageStats.videos_total?.toLocaleString() ?? '—'}</div>
                    </div>
                    <div className="customer-tile">
                      <div className="tile-label">Activation rate</div>
                      <div className="tile-value">
                        {usageStats.activation_rate != null
                          ? `${Math.round(usageStats.activation_rate * 100)}%`
                          : '—'}
                      </div>
                    </div>
                  </div>
                )}

                {/* Time-series chart */}
                <div className="usage-chart-controls">
                  <label>
                    Metric
                    <select
                      className="stats-chart-select"
                      value={usageMetric}
                      onChange={e => setUsageMetric(e.target.value as MetricKey)}
                    >
                      {METRICS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                  </label>
                  <label>
                    Bucket
                    <select
                      className="stats-chart-select"
                      value={usageBucket}
                      onChange={e => setUsageBucket(e.target.value as 'day' | 'week' | 'month')}
                    >
                      {BUCKETS.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
                    </select>
                  </label>
                </div>

                {usageSeriesLoading && !usageSeries && <div className="muted">Loading time-series…</div>}
                {usageSeriesUnavailable && (
                  <p className="stats-chart-empty">Time-series endpoint not available yet.</p>
                )}
                {usageSeries && (
                  <ActivityChart
                    points={usageSeries.points || []}
                    metric={usageMetric}
                    metricLabel={METRICS.find(m => m.value === usageMetric)?.label || usageMetric}
                  />
                )}

                {/* Escalation categories breakdown */}
                {usageStats?.escalation_categories && Object.keys(usageStats.escalation_categories).length > 0 && (
                  <div className="usage-breakdown">
                    <h5>Escalation categories</h5>
                    <ul>
                      {Object.entries(usageStats.escalation_categories as Record<string, number>)
                        .sort((a, b) => b[1] - a[1])
                        .map(([k, v]) => (
                          <li key={k}>
                            <span className="breakdown-label">{k}</span>
                            <span className="breakdown-value">{v.toLocaleString()}</span>
                          </li>
                        ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Phase 3 — Generate Email panel */}
              <div className="customer-detail-section">
                <h4>Generate Outreach Email</h4>
                {templates.length === 0 ? (
                  <div className="muted">No email templates configured.</div>
                ) : (
                  <div className="email-panel">
                    <div className="email-panel-controls">
                      <label className="email-template-label">
                        Template
                        <select
                          value={selectedTemplateId ?? ''}
                          onChange={e => setSelectedTemplateId(Number(e.target.value))}
                        >
                          {templates.map(t => (
                            <option key={t.id} value={t.id}>{t.label}</option>
                          ))}
                        </select>
                      </label>
                      {rendered && rendered.placeholder_count > 0 && (
                        <span className="placeholder-warning">
                          ⚠ {rendered.placeholder_count} placeholder{rendered.placeholder_count === 1 ? '' : 's'} to fill
                        </span>
                      )}
                    </div>

                    {renderError && <Alert type="error">{renderError}</Alert>}

                    {renderLoading && !rendered && <div className="muted">Rendering…</div>}

                    {rendered && (
                      <>
                        <div className="email-preview">
                          <div className="email-subject">
                            <strong>Subject:</strong> {rendered.subject}
                          </div>
                          <div
                            className="email-body"
                            dangerouslySetInnerHTML={{ __html: rendered.body_html }}
                          />
                        </div>
                        <div className="email-panel-actions">
                          <Button variant="primary" size="sm" onClick={copyHtml}>Copy Email Body</Button>
                          <Button variant="secondary" size="sm" onClick={copySubject}>Copy Subject</Button>
                          {copyStatus && <span className="copy-status">{copyStatus}</span>}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default CustomerHealthDashboard;
